import type { ComponentProps, ReactElement, ReactNode } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

type NativeSelectProps = Omit<
  ComponentProps<typeof Select>,
  'name' | 'value' | 'onChange' | 'onBlur' | 'ref'
>;
export interface SelectOption {
  value: string;
  label: string;
}

export interface FormSelectInputProps<TFieldValues extends FieldValues>
  extends NativeSelectProps {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  className?: string;
  labelClassName?: string;
  options: Array<SelectOption>;
  placeholder?: string;
  isLoading?: boolean;
  icon?: ReactNode;
}
export const FormSelectInput = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  className,
  labelClassName,
  options,
  placeholder,
  isLoading = false,
  icon,
  ...selectProps
}: FormSelectInputProps<TFieldValues>): ReactElement => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem className={className}>
        <FormLabel
          className={cn(
            'text-xs font-medium uppercase text-muted-foreground',
            labelClassName
          )}>
          {label}
        </FormLabel>
        <Select
          {...selectProps}
          disabled={!!selectProps.disabled || isLoading}
          onValueChange={field.onChange}
          value={field.value ?? ''}>
          <FormControl>
            <SelectTrigger className='w-full min-w-0'>
              <span className='flex min-w-0 items-center gap-2'>
                {isLoading && <Spinner />}
                {!isLoading && icon}
                <SelectValue placeholder={placeholder} />
              </span>
            </SelectTrigger>
          </FormControl>
          <SelectContent className='max-h-72'>
            {options.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    )}
  />
);
